import { Component, type ErrorInfo, type ReactNode } from 'react'
import { logError } from '../lib/logger'
import { monitoringService } from '../services/monitoringService'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  source?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logError(error, {
      source: this.props.source ?? 'error-boundary',
      extra: { componentStack: info.componentStack },
    })
    monitoringService.reportFrontendError({
      severity: 'ERROR',
      endpoint: window.location.pathname,
      message: error.message,
      stack_trace: error.stack ?? null,
      extra_data: { component_stack: info.componentStack, browser: navigator.userAgent },
    })
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm font-medium text-gray-900">Something went wrong.</p>
          <p className="max-w-md text-sm text-gray-500">
            We hit an unexpected error rendering this page. You can retry, or refresh if the problem keeps happening.
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            className="mt-1 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
